import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { uploadBulkOnboardingAction } from "../../actions/uploadBulkOnboardingAction";
import { uploadOnboardingDocumentsAction } from "../../actions/uploadOnboardingDocumentsAction";
import { fetchBulkOnboardingListAction } from "../../actions/fetchBulkOnboardingListAction";
import { fetchOnboardingDocumentListAction } from "../../actions/fetchOnboardingDocumentListAction";
import { viewOnboardingDocumentAction } from "../../actions/viewOnboardingDocumentAction";
import { resendKycLinkAction } from "../../actions/resendKycLinkAction";

const PAGE_SIZE = 10;

export const useUploadExcelSheet = () => {
  // ── Upload modal state ──────────────────────────────────────────────────────
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadType, setUploadType] = useState(""); // "folder" | "excel"
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // 0-100

  // ── Validation results (from upload-bulk-onboarding response) ──────────────
  // { message: string, data: [{ vendorId, validationMessage, isValid }] }
  const [validationResults, setValidationResults] = useState(null);

  // ── Table / pagination state ────────────────────────────────────────────────
  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const debounceRef = useRef(null);

  // ── Document viewer modal state ─────────────────────────────────────────────
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docModalRow, setDocModalRow] = useState(null); // the row whose docs we're viewing
  const [docList, setDocList] = useState([]); // array from API
  const [docListLoading, setDocListLoading] = useState(false);
  // per-doc blob url cache: { [documentId]: { url, contentType, loading } }
  const [docBlobs, setDocBlobs] = useState({});

  // ── Fetch list from API ─────────────────────────────────────────────────────
  const loadList = useCallback(async (page = 1, search = "") => {
    setLoading(true);
    try {
      const result = await fetchBulkOnboardingListAction({
        searchValue: search || null,
        pageNumber: page,
        pageSize: PAGE_SIZE,
      });
      if (result.success) {
        setTableData(result.data);
        setTotalRecords(result.totalRecords);
      } else {
        toast.error(result.message || "Failed to load list");
        setTableData([]);
        setTotalRecords(0);
      }
    } catch {
      toast.error("Failed to load list");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount + page change
  useEffect(() => {
    loadList(currentPage, searchQuery);
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  const handleSearchChange = (v) => {
    setSearchQuery(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      loadList(1, v);
    }, 400);
  };

  // ── Upload modal handlers ───────────────────────────────────────────────────
  const handleOpenModal = (type) => {
    setFile(null);
    setUploadType(type);
    setUploadModalOpen(true);
  };
  const handleCloseModal = () => {
    setUploadModalOpen(false);
    setUploadType("");
    setFile(null);
    setUploadProgress(0);
  };
  const handleFileChange = (e) => {
    if (e.target.files[0]) setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const onProgress = (pct) => setUploadProgress(pct);
      let result;

      if (uploadType === "folder") {
        // Folder upload — no validation table, just success/error toast
        result = await uploadOnboardingDocumentsAction(file, onProgress);
        if (result.success) {
          setUploadProgress(100);
          toast.success(result.message || "Documents uploaded successfully");
          setTimeout(() => {
            handleCloseModal();
          }, 600);
        } else {
          toast.error(result.message || "Upload failed — please try again");
          setUploadProgress(0);
        }
      } else {
        // Excel upload — API returns data[] with validation rows on both success and failure
        result = await uploadBulkOnboardingAction(file, onProgress);
        console.log(
          "[UploadExcel] result:",
          JSON.stringify({
            success: result.success,
            message: result.message,
            dataLength: result.data?.length,
          }),
        );
        setUploadProgress(100);

        const hasValidationData =
          Array.isArray(result.data) && result.data.length > 0;

        if (hasValidationData) {
          // Set validation results FIRST, then close modal
          setValidationResults({ message: result.message, data: result.data });
          handleCloseModal(); // close immediately — results are already in state
          if (result.success) {
            toast.success(result.message || "Upload complete");
          } else {
            toast.warning(
              result.message || "Upload completed with validation errors",
            );
          }
        } else if (result.success) {
          toast.success(result.message || "Uploaded successfully");
          setTimeout(() => {
            handleCloseModal();
            setCurrentPage(1);
            loadList(1, searchQuery);
          }, 600);
        } else {
          toast.error(result.message || "Upload failed — please try again");
          setUploadProgress(0);
        }
      }
    } catch {
      toast.error("An error occurred during upload");
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  // ── View Documents modal ────────────────────────────────────────────────────
  const handleViewDocuments = async (row) => {
    const bulkUploadId = row.id;
    if (!bulkUploadId) {
      toast.error("No bulk upload ID found for this row");
      return;
    }
    setDocModalRow(row);
    setDocList([]);
    setDocBlobs({});
    setDocModalOpen(true);
    setDocListLoading(true);
    try {
      const result = await fetchOnboardingDocumentListAction(bulkUploadId);
      if (result.success && result.data.length > 0) {
        setDocList(result.data);
        // Pre-load blob URLs for all documents
        result.data.forEach((doc) => loadDocBlob(doc.id));
      } else {
        setDocList([]);
        if (!result.success)
          toast.error(result.message || "Failed to fetch documents");
      }
    } catch {
      toast.error("Failed to fetch documents");
    } finally {
      setDocListLoading(false);
    }
  };

  const loadDocBlob = async (documentId) => {
    setDocBlobs((prev) => ({
      ...prev,
      [documentId]: { url: null, contentType: null, loading: true },
    }));
    try {
      const result = await viewOnboardingDocumentAction(documentId);
      if (result.success) {
        setDocBlobs((prev) => ({
          ...prev,
          [documentId]: {
            url: result.url,
            contentType: result.contentType,
            loading: false,
          },
        }));
      } else {
        setDocBlobs((prev) => ({
          ...prev,
          [documentId]: {
            url: null,
            contentType: null,
            loading: false,
            error: true,
          },
        }));
      }
    } catch {
      setDocBlobs((prev) => ({
        ...prev,
        [documentId]: {
          url: null,
          contentType: null,
          loading: false,
          error: true,
        },
      }));
    }
  };

  const handleCloseDocModal = () => {
    setDocModalOpen(false);
    setDocModalRow(null);
    setDocList([]);
    // Revoke blob URLs to free memory
    Object.values(docBlobs).forEach((b) => {
      if (b?.url) URL.revokeObjectURL(b.url);
    });
    setDocBlobs({});
  };

  // ── Resend KYC ──────────────────────────────────────────────────────────────
  const handleResend = async (row) => {
    const id = row.entityPrimaryId || row.id;
    if (!id) {
      toast.error("Entity ID not found");
      return;
    }
    const result = await resendKycLinkAction(id);
    if (result.success) toast.success(result.message || "KYC link resent!");
    else toast.error(result.message || "Failed to resend KYC link");
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));

  return {
    uploadModalOpen,
    uploadType,
    handleOpenModal,
    handleCloseModal,
    file,
    handleFileChange,
    uploading,
    uploadProgress,
    handleUpload,
    tableData,
    totalRecords,
    totalPages,
    currentPage,
    setCurrentPage,
    loading,
    searchQuery,
    handleSearchChange,
    handleResend,
    PAGE_SIZE,
    // validation results
    validationResults,
    clearValidationResults: () => setValidationResults(null),
    // doc viewer
    docModalOpen,
    docModalRow,
    docList,
    docListLoading,
    docBlobs,
    handleViewDocuments,
    handleCloseDocModal,
  };
};
